import React, { useCallback, useState } from 'react';

import {
  isAnnounced,
  isFundRaising,
  OwnedInvestment as MyInvestment,
  Reservation,
} from 'api-client';
import { ProjectsCount } from 'api-client/models/project/ProjectsCount';
import download from 'assets/images/svg/download.svg';
import { AccountNotReadyWarningSection, DataTable, Pagination } from 'components';
import ProjectsFilter, {
  Filter,
} from 'components/pages/projects/filter/ProjectsFilter/ProjectsFilter';
import { defaultProjectsFilter } from 'components/pages/projects/filter/ProjectsFilter/utils';
import { ProjectsTableFilterBar } from 'components/pages/projects/filter/ProjectsTableFilterBar';
import { RoiIrrHint } from 'components/pages/projects/RoiIrrHint';
import apiClientConfig from 'configs/app';
import { createInvestmentManager, createReservationManager } from 'di';
import { useErrorCallback, useToggle } from 'hooks';
import { DataCollection } from 'utils';
import ProjectsContextProvider, {
  ProjectsContextType,
} from 'views/dashboard/lender/investment/projects/ProjectsContext';

import { MyInvestmentMobileCard } from '../MyInvestmentMobileCard';
import { MyInvestmentTableRow } from '../MyInvestmentTableRow';
import { MyReservationMobileCard } from '../MyReservationMobileCard';
import { MyReservationTableRow } from '../MyReservationTableRow';

import './MyInvestments.scoped.scss';

const TABLE_COLUMNS = '1.6fr 128px 1.2fr 1.2fr 1.1fr 2.2fr 2.2fr';

const MyInvestments = () => {
  const errorCallback = useErrorCallback();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [myReservations, setMyReservations] = useState<Reservation[]>([]);
  const [myInvestments, setMyInvestments] = useState<DataCollection<MyInvestment>>(
    new DataCollection()
  );
  const [filter, setFilter] = useState<Filter>(defaultProjectsFilter);
  const [filterActive, toggleFilter] = useToggle();
  const [projectsCount, setProjectsCount] = useState<ProjectsCount>({
    closed: 0,
    current: 0,
    fundRaising: 0,
    autoInvestment: 0,
    refunded: 0,
  });

  const loadData = useCallback(
    async (page: number, perPage: number): Promise<void> => {
      setLoading(true);
      try {
        const [manager, reservationManager] = await Promise.all([
          createInvestmentManager(),
          createReservationManager(),
        ]);
        const dataCollection = new DataCollection<MyInvestment>();
        const [investments, reservations, projectsCountResponse] = await Promise.all([
          manager.getListOwnedInvestments(page, perPage, filter),
          reservationManager.getAll(),
          manager.getProjectsCount(),
        ]);
        dataCollection.setCollection(investments);
        setMyInvestments(dataCollection);
        setProjectsCount(projectsCountResponse);
        setMyReservations(
          reservations.filter(
            (r) => r.project && (isAnnounced(r.project.status) || isFundRaising(r.project.status))
          )
        );
      } catch (err) {
        if (err instanceof Error) await errorCallback(err);
      } finally {
        setLoading(false);
      }
    },
    [filter]
  );

  const value: ProjectsContextType = {
    filter,
    setFilter,
    filterActive,
    toggleFilter,
  };

  const exportStatistics = async (): Promise<void> => {
    setExportLoading(true);
    try {
      const manager = await createInvestmentManager();
      const response = await manager.exportListOwnedInvestments(filter);
      window.open(apiClientConfig.apiBaseUrl + response.url, '_blank');
    } catch (err) {
      if (err instanceof Error) await errorCallback(err);
    } finally {
      setExportLoading(false);
    }
  };

  const visibleInvestments = myInvestments.items.filter(
    (item: MyInvestment) => !isAnnounced(item.project.status)
  );
  const isEmpty = visibleInvestments.length === 0 && myReservations.length === 0;

  return (
    <Pagination
      onLoad={loadData}
      count={myInvestments.count}
      totalCount={myInvestments.totalCount}
      filterObject={filter}
      initialChangeFilter={(value: Partial<Filter>) => setFilter({ ...filter, ...value })}
    >
      <AccountNotReadyWarningSection />
      <ProjectsContextProvider props={value}>
        <div className="my-investments">
          <div className="my-investments__toolbar">
            <ProjectsTableFilterBar
              projectsCount={projectsCount}
              mode="myInvestments"
              onExport={exportStatistics}
              exportLoading={exportLoading}
              exportIconSrc={download}
            />
            <ProjectsFilter />
          </div>
          <Pagination.Content loading={loading}>
            <div className="my-investments__desktop">
              <DataTable
                variant="surface"
                columns={TABLE_COLUMNS}
                className="my-investments__table"
              >
                <DataTable.Head>
                  <DataTable.HeadCell>Проект</DataTable.HeadCell>
                  <DataTable.HeadCell align="center">Статус</DataTable.HeadCell>
                  <DataTable.HeadCell>Инвестировано</DataTable.HeadCell>
                  <DataTable.HeadCell>Прогноз возврата</DataTable.HeadCell>
                  <DataTable.HeadCell>
                    <RoiIrrHint>Прогноз ROI / IRR</RoiIrrHint>
                  </DataTable.HeadCell>
                  <DataTable.HeadCell />
                  <DataTable.HeadCell />
                </DataTable.Head>
                <DataTable.Body>
                  {isEmpty ? (
                    <DataTable.Empty>Сделки не найдены</DataTable.Empty>
                  ) : (
                    <>
                      {myReservations.map((reservation) => (
                        <MyReservationTableRow reservation={reservation} key={reservation.uuid} />
                      ))}
                      {visibleInvestments.map((item: MyInvestment) => (
                        <MyInvestmentTableRow investment={item} key={item.project.uuid} />
                      ))}
                    </>
                  )}
                </DataTable.Body>
              </DataTable>
            </div>
            <div className="my-investments__mobile">
              {isEmpty ? (
                <div className="my-investments__empty">Сделки не найдены</div>
              ) : (
                <>
                  {myReservations.map((reservation) => (
                    <MyReservationMobileCard reservation={reservation} key={reservation.uuid} />
                  ))}
                  {visibleInvestments.map((item: MyInvestment) => (
                    <MyInvestmentMobileCard investment={item} key={item.project.uuid} />
                  ))}
                </>
              )}
            </div>
          </Pagination.Content>
        </div>
        <Pagination.Paginator />
      </ProjectsContextProvider>
    </Pagination>
  );
};

export default MyInvestments;
